import { COINS_DISABLED } from '../config/coinsFlag';
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { markWalletReturnContext } from '../lib/routeStateRestore';

/**
 * Wassla Wallet Button (premium)
 * - Designed for dark/green headers
 * - Golden wallet with soft highlights
 * - Optional coins badge
 */
export default function WasslaWalletButton({
  coins = 0,
  className = "",
  title = "Wallet",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  if (COINS_DISABLED) return null;

  const openWallet = () => {
    const isWalletRoute = location.pathname.startsWith('/wallet');
    if (isWalletRoute) {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number' && idx > 0) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    markWalletReturnContext(location.pathname);
    navigate("/wallet", { state: { from: location.pathname } });
  };
  
  return (
    <button
      type="button"
      onClick={openWallet}
      aria-label={title}
      data-testid="coins-wallet"
      className={`wassla-wallet-btn ${className}`}
      style={{
        boxShadow: "none",
      }}
    >
      <WasslaWalletIcon size={17} />

      <span className="wallet-coin-count">{coins > 999 ? '999+' : coins}</span>

      <style>{`
        .wassla-wallet-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          height: 32px;
          gap: 5px;
          border-radius: 9999px;
          padding: 0 8px;
          background: #f3f4f6;
          border: 1px solid rgba(17,24,39,0.08);
          cursor: pointer;
          box-shadow: none;
          transition: all 150ms ease;
          line-height: 1;
        }
        
        .wassla-wallet-btn:hover {
          background: #e5e7eb;
        }
        
        .wassla-wallet-btn:active {
          background: #d1d5db;
          transform: scale(0.98);
        }
        
        .wallet-coin-count {
          font-size: 12px;
          font-weight: 600;
          color: #111827;
          line-height: 1;
        }

        html.dark .wassla-wallet-btn {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.12);
          box-shadow: none;
        }
        html.dark .wassla-wallet-btn:hover {
          background: rgba(255,255,255,0.12);
        }
        html.dark .wallet-coin-count {
          color: rgba(255,255,255,0.9);
        }
        
      `}</style>
    </button>
  );
}

/**
 * Premium wallet icon tuned for Wassla green header.
 * Golden wallet + subtle green harmony in shadows/highlights.
 */
function WasslaWalletIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.22))" }}
    >
      <defs>
        {/* Cash notes (soft mint that fits your green header) */}
        <linearGradient id="w_cash" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#E9FFF0" />
          <stop offset="1" stopColor="#A7EBC0" />
        </linearGradient>

        {/* Wallet body (premium gold / leather blend) */}
        <linearGradient id="w_wallet" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F5C84C" />
          <stop offset="0.55" stopColor="#D9A51A" />
          <stop offset="1" stopColor="#8A5A10" />
        </linearGradient>

        {/* Wallet inner flap (darker gold-brown) */}
        <linearGradient id="w_flap" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7A4B0A" />
          <stop offset="1" stopColor="#4A2D08" />
        </linearGradient>

        {/* Stitch line (warm cream) */}
        <linearGradient id="w_stitch" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FFF2D1" />
          <stop offset="1" stopColor="#EBD7A7" />
        </linearGradient>

        {/* Button coin (strong gold) */}
        <linearGradient id="w_coin" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFE08A" />
          <stop offset="1" stopColor="#B67D00" />
        </linearGradient>

        {/* Soft green harmony highlight */}
        <radialGradient id="w_greenGlow" cx="50%" cy="35%" r="60%">
          <stop offset="0" stopColor="#18C37E" stopOpacity="0.22" />
          <stop offset="1" stopColor="#18C37E" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* subtle green harmony glow behind */}
      <circle cx="34" cy="30" r="26" fill="url(#w_greenGlow)" />

      {/* cash notes */}
      <g>
        <path
          d="M18 10c0-2 1.6-4 4-4h18c2.4 0 4 2 4 4v18c0 2-1.6 4-4 4H22c-2.4 0-4-2-4-4V10z"
          fill="url(#w_cash)"
          opacity="0.95"
        />
        <path
          d="M22 12h18c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H22c-1.1 0-2-.9-2-2V14c0-1.1.9-2 2-2z"
          fill="none"
          stroke="#4FCB88"
          strokeWidth="2"
          opacity="0.85"
        />
        <circle cx="31" cy="21" r="4.5" fill="#6BDA98" opacity="0.9" />
      </g>

      {/* wallet body */}
      <g>
        <path
          d="M14 24c0-4 3.2-7 7-7h22c5.5 0 9 3.5 9 9v18c0 6-4 10-10 10H22c-4.4 0-8-3.6-8-8V24z"
          fill="url(#w_wallet)"
        />

        {/* flap */}
        <path
          d="M43 24h10c2 0 3 1.3 3 3v10c0 2-1 3-3 3H43c-3.5 0-6-2.2-6-6v-4c0-3.8 2.5-6 6-6z"
          fill="url(#w_flap)"
          opacity="0.98"
        />

        {/* button */}
        <circle cx="50" cy="34" r="3.6" fill="url(#w_coin)" />
        <circle cx="50" cy="34" r="1.2" fill="#2E1B05" opacity="0.4" />

        {/* stitches */}
        <rect
          x="18"
          y="22"
          width="34"
          height="30"
          rx="8"
          fill="none"
          stroke="url(#w_stitch)"
          strokeWidth="2"
          strokeDasharray="4 4"
          opacity="0.75"
        />

        {/* top highlight */}
        <path
          d="M18 28c4-6 12-9 22-7 4 .8 7 2.3 9 4"
          fill="none"
          stroke="rgba(255,255,255,0.28)"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

export { WasslaWalletIcon };
